/**
 * Created by Yoyo Fang on 11/10/2020.
 * Description:
 * Intralinks for Admin Console
 * ------ maintenance history ------
 * Updated by Daniel Wang on 04/10/2022 Moved the intralins configuration to admin console from admin page
 */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { createSelector, select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import * as _lodash from 'lodash';

import { AppState } from '../../../../redux';
import { IntralinksAdminVisibility } from '../../../../tamalelibs/redux/intralinks.actions';
import { IntralinksSetting } from '../../../../models/global-setting';
import { businessConstants } from '../../../../constants/business.constants';
import { lastCustomActionSelector } from '../../../../redux/reducers/action-history.reducer';
import { WorkflowActionTypes } from '../../../../tamalelibs/redux/actions/workflow.actions';

@Component({
    selector: 'tam-admin-intralinks',
    templateUrl: './admin-intralinks.component.html',
    styleUrls: ['./admin-intralinks.component.scss']
})
export class AdminIntralinksComponent implements OnInit, OnDestroy {
    intralinks: IntralinksSetting;
    intralinksOriginal: IntralinksSetting; // store the original values
    intralinsManagementForm = new UntypedFormGroup({
        serverUrl: new UntypedFormControl(''),
        uiUrl: new UntypedFormControl(''),
    });
    isProgressing = false;

    get serverUrl() {
        return this.intralinsManagementForm.get(businessConstants.intralinksManage.serverUrl);
    }
    get uiUrl() {
        return this.intralinsManagementForm.get(businessConstants.intralinksManage.uiUrl);
    }

    isHideIntralinks = false;

    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _store: Store<AppState>,
    ) {
    }

    ngOnInit() {
        this._initSubscriptions();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    /*
    * enable or disable intralinks status change
    */
    changeIntralinksStatus(hide: boolean) {
        this.isHideIntralinks = hide;
        this._doValidation();
        this._setValuesByIntralinksStatus(this.intralinksOriginal);
        const payload = {
            intralinks: this.intralinks,
            isOnlyEnable: true
        };
        this._store.dispatch(new IntralinksAdminVisibility(payload));
    }

    /*
    *  verify contorls' status if the control's value is changed
    */
    configChanged() {
        this._doValidation();
    }

    /*
    *  save the change to server
    */
    onSave() {
        this.isProgressing = true;
        this.intralinks.enable = !this.isHideIntralinks;
        this.intralinks.uiUrl = this.uiUrl.value;
        this.intralinks.serverUrl = this.serverUrl.value;

        const payload = {
            intralinks: this.intralinks,
            isOnlyEnable: false
        };
        if (this.isHideIntralinks) {
            this._store.dispatch(new IntralinksAdminVisibility(payload));
        } else if (this._doValidation()) {
            this._store.dispatch(new IntralinksAdminVisibility(payload));
        }
    }

    /*
    *  check the controls' status
    */
    private _doValidation(): boolean {
        let dataValid = true;

        if (!this.uiUrl.value) {
            this.uiUrl.setErrors({
                required: true
            });
            dataValid = false;
        }
        if (!this.serverUrl.value) {
            this.serverUrl.setErrors({
                required: true
            });
            dataValid = false;
        }

        return dataValid;
    }

    /*
    *  init the information for the controls
    */
    private _initIntralinksInformation(intralinks: IntralinksSetting) {
        this.intralinks = intralinks;
        this.intralinksOriginal = _lodash.cloneDeep(intralinks);
        this.isHideIntralinks = !this.intralinks.enable;
        this.isProgressing = false;
        this._setValuesByIntralinksStatus(this.intralinks);
    }

    /*
    *  listen to the change of intralins
    */
    private _initSubscriptions() {
        this._destroySubscriptions.push(
            // get intralinks information from global config in store
            this._store.pipe(
                select(createSelector(state => state[businessConstants.globalConfig.systemConfig], (state) => state ? state[businessConstants.globalConfig.intralinks] : null)),
                filter(state => !!state)
            ).subscribe(intralinks => {
                this._initIntralinksInformation(intralinks);
            }),
            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => {
                this._onSubscribeActionMessage(action);
            }),
        );
    }


    /**
     * handle request error case.
     *
     * @private
     * @param {*} action
     * @memberof AdminIntralinksComponent
     */
    private _onSubscribeActionMessage(action) {
        if (action.type && action.type === WorkflowActionTypes.ADMIN_VISIBILITY_CHANGE_FAILED) {
            this.isProgressing = false;
        }
    }

    /*
    *  set values for controls by the status of intralinks
    */
    private _setValuesByIntralinksStatus(intralinks: IntralinksSetting): void {
        if (this.isHideIntralinks) {
            this.uiUrl.setValue('');
            this.serverUrl.setValue('');
            this.intralinks.enable = false;
            this.uiUrl.disable();
            this.serverUrl.disable();
        } else {
            this.uiUrl.enable();
            this.serverUrl.enable();
            if (intralinks) {
                this.intralinks = intralinks;
                this.intralinks.enable = true;
                this.uiUrl.setValue(this.intralinks.uiUrl);
                this.serverUrl.setValue(this.intralinks.serverUrl);
            }
        }
    }
}
